/**
 * A successful command makes the reads stale. This is what says so.
 *
 * `useRead` asks again when its question changes, and «someone just revoked that record» is not part
 * of any question. Before this, a revoke succeeded, the backend held the new state, and the register
 * went on showing the old one with its «İptal et» button still enabled until the reader changed a
 * filter or reloaded the page by hand.
 *
 * The rule is deliberately narrow. One bump per command that CHANGED something, and nothing else —
 * not a store write, not a render, not a read. Bumping on store writes would turn every adoption into
 * a fresh network request and rebuild the loop this package exists to remove.
 *
 * It wraps whichever implementation is mounted, so the demo's in-memory mutations and the product's
 * authorized routes get the same correctness from the same place.
 */
import type { V07Commands } from './commands';

/**
 * The methods that read or only touch local screen state, and therefore change no answers.
 *
 * `hydrateCredential` is the important one: it is the detail screen's own read, and bumping after it
 * would invalidate the read that asked for it — a loop, one step longer than the obvious one.
 */
const READ_ONLY = new Set<string>([
  'hydrateCredential',
  'listUsers',
  'patchLocal',
  'refresh',
  /* Telemetry for the LinkedIn helper; it changes no list, no total and no lifecycle state. */
  'recordLinkedInStep',
  /*
   * The same trap `hydrateCredential` is here for, one screen further on.
   *
   * «Kayıt ve hareket geçmişi» re-reads when the revision moves, because a command that changed the
   * record changed its history too. Its own read goes through this layer like every other command,
   * so bumping after it would invalidate the read that asked for it — and the card would fetch
   * forever. It is a read; it changes nothing; it does not bump.
   */
  'readCredentialHistory',
  /*
   * The same trap again, for the first-send surfaces. The preview is a question about what a send
   * would do and the request read is a question about one that was accepted; neither changes
   * anything, and bumping after them would make the dialog asking the question re-read and ask it
   * again, for ever. `requestFirstSend` is deliberately NOT here: it accepts a send.
   */
  'previewFirstSend',
  /*
   * …and the same trap for the bulk acceptance-link window. The preview is a question about what
   * the act would do; `createBulkClaimLinks` is deliberately NOT here, because it rotates tokens
   * and every list showing a record's claim state has just gone stale.
   */
  'previewBulkClaimLinks',
  'readFirstSendRequest',
  /*
   * …and the list of requests already accepted, which is the same trap once more. The record's
   * detail screen reads it to decide whether to offer «İlk e-posta gönderim sonucu», and that read
   * runs whenever the revision moves. Bumping after it moves the revision, which re-runs the read,
   * which bumps again — a loop the demo store's write limit caught on the very first full run.
   */
  'listFirstSendRequests',
  'readSheetSync',
  /* A download composes a file from what is already there and changes no answer on any screen. */
  'exportCredentials',
  'exportCredentialHistory',
  /*
   * Usage measurement. It records that somebody pressed something; it changes no list, no total and
   * no lifecycle state, and a copied link must not cost the page a re-read.
   */
  'recordInteraction',
]);

/** A command answered without refusing. `patchLocal` returns nothing, and is excluded above anyway. */
function succeeded(result: unknown): boolean {
  if (result === null || typeof result !== 'object') return false;
  return (result as { ok?: unknown }).ok !== false;
}

export function withReadInvalidation(commands: V07Commands, invalidate: () => void): V07Commands {
  const out: Record<string, unknown> = {};
  for (const key of Reflect.ownKeys(commands) as string[]) {
    const value = (commands as unknown as Record<string, unknown>)[key];
    if (typeof value !== 'function' || READ_ONLY.has(key)) {
      out[key] = value;
      continue;
    }
    const fn = value as (...args: unknown[]) => unknown;
    out[key] = (...args: unknown[]) => {
      const result = fn.apply(commands, args);
      if (result instanceof Promise) {
        return result.then((settled) => {
          if (succeeded(settled)) invalidate();
          return settled;
        });
      }
      /* A synchronous command that returned at all has done whatever it does. */
      invalidate();
      return result;
    };
  }
  return out as unknown as V07Commands;
}
